import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Activity, ShieldCheck, AlertTriangle, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { timeAgo } from '../utils/relativeTime';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only digest: surfaces recent activity, permission changes, and
 * high-risk roster summary on the dashboard so admins don't have to dig.
 *
 * No new endpoints — uses GET /api/logs, GET /api/analytics/academy/overview.
 */
const AdminDigest = () => {
  const navigate = useNavigate();
  const [recent, setRecent] = useState([]);
  const [permChanges, setPermChanges] = useState([]);
  const [highRisk, setHighRisk] = useState({ count: 0, top: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    Promise.all([
      axios.get(`${API}/api/logs?page_size=6`, h).catch(() => ({ data: { logs: [] } })),
      axios.get(`${API}/api/logs?module=access_control&page_size=4`, h).catch(() => ({ data: { logs: [] } })),
      axios.get(`${API}/api/analytics/academy/overview`, h).catch(() => ({ data: null })),
    ]).then(([r, p, a]) => {
      setRecent(r.data?.logs || []);
      setPermChanges(p.data?.logs || []);
      setHighRisk({
        count: a.data?.high_risk_count || 0,
        top: (a.data?.high_risk_athletes || []).slice(0, 3),
      });
    }).finally(() => setLoading(false));
  }, []);

  const renderLogLine = (l, idx) => (
    <li
      key={l.id || idx}
      className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-gray-50 dark:border-gray-800 last:border-b-0"
      data-testid={`digest-log-${l.id || idx}`}
    >
      <div className="min-w-0">
        <p className="text-gray-800 dark:text-gray-200 truncate">
          {l.description || `${l.action || ''} ${l.module || ''}`}
        </p>
        <p className="text-[10px] text-gray-400 truncate">
          {l.user_email || 'system'} · {timeAgo(l.timestamp)}
        </p>
      </div>
    </li>
  );

  return (
    <Card className="bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700" data-testid="admin-digest">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Activity size={14} className="text-emerald-600" />
          Admin Digest
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <p className="text-xs text-gray-400 text-center py-6">Loading…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Recent activity */}
            <div data-testid="digest-recent-activity">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Recent activity</p>
                <button
                  onClick={() => navigate('/system-logs')}
                  className="text-[10px] text-pfap-green hover:underline flex items-center"
                  data-testid="digest-recent-open"
                >
                  View all <ChevronRight size={10} />
                </button>
              </div>
              {recent.length === 0 ? (
                <p className="text-xs text-gray-400 py-2">No recent activity.</p>
              ) : (
                <ul>{recent.slice(0, 6).map(renderLogLine)}</ul>
              )}
            </div>

            {/* Permission changes */}
            <div data-testid="digest-permission-changes">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <ShieldCheck size={10} /> Permission changes
                </p>
                <button
                  onClick={() => navigate('/admin/access-control')}
                  className="text-[10px] text-pfap-green hover:underline flex items-center"
                  data-testid="digest-permissions-open"
                >
                  Manage <ChevronRight size={10} />
                </button>
              </div>
              {permChanges.length === 0 ? (
                <p className="text-xs text-gray-400 py-2">No recent changes.</p>
              ) : (
                <ul>{permChanges.map(renderLogLine)}</ul>
              )}
            </div>

            {/* High-risk summary */}
            <div data-testid="digest-high-risk">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <AlertTriangle size={10} className="text-amber-500" /> High-risk
                </p>
                <button
                  onClick={() => navigate('/analytics/academy')}
                  className="text-[10px] text-pfap-green hover:underline flex items-center"
                  data-testid="digest-high-risk-open"
                >
                  Overview <ChevronRight size={10} />
                </button>
              </div>
              <p className="text-2xl font-semibold text-amber-600 dark:text-amber-400 tabular-nums" data-testid="digest-high-risk-count">
                {highRisk.count}
              </p>
              <p className="text-[10px] text-gray-400 mb-1">athlete{highRisk.count === 1 ? '' : 's'} below threshold</p>
              {highRisk.top.length === 0 ? (
                <p className="text-xs text-gray-400">All clear.</p>
              ) : (
                <ul>
                  {highRisk.top.map((a) => (
                    <li
                      key={a.athlete_id}
                      className="flex items-center justify-between gap-2 text-xs py-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/30 -mx-1 px-1 rounded"
                      onClick={() => navigate(`/athlete/${a.athlete_id}/360`)}
                      data-testid={`digest-high-risk-${a.athlete_id}`}
                    >
                      <span className="truncate text-gray-800 dark:text-gray-200">{a.athlete_name}</span>
                      <span className="text-[10px] text-gray-400">{a.squad || ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminDigest;
